export const guides = [
  {
    id: "platform-runaway-evidence",
    title: "平台突然跑路，先把这 5 类证据固定好",
    category: "维权流程",
    categoryKey: "process",
    summary:
      "平台一旦停止运营，证据往往最先消失。这份清单告诉你在第一时间应该保存哪些材料，以及按什么顺序保存最有效。",
    readMinutes: 8,
    updatedAt: "2026-08-28",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20about%20collecting%20evidence%20documents%2C%20stacked%20paper%20sheets%20with%20checkmark%20and%20lens%20shapes%2C%20warm%20amber%20and%20charcoal%20palette%2C%20minimal%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text&width=1000&height=640&seq=guide-cover-evidence-2026&orientation=landscape",
    sections: [
      {
        heading: "为什么证据固定要抢时间",
        paragraphs: [
          "平台停止运营后，App 会停止服务、社群会被解散、客服入口会失效，很多关键信息在几天内就无法访问。因此在得知异常消息后的第一时间，先把能证明交易关系和损失的材料保存下来，比反复催促客服更重要。",
          "保存时优先选择“不可篡改”的形式：能导出 PDF 就导出，能录屏就录屏，截图要包含完整界面信息，包括时间、账号、订单号与金额。"
        ],
        bullets: [
          "支付与订单记录：订单号、金额、时间、支付渠道流水",
          "沟通记录：客服对话、群聊记录、推广话术截图或录屏",
          "账户信息：账号、余额、提现记录、会员等级页面",
          "平台信息：页面截图、公告、服务条款与主体信息展示",
          "关联信息：对接人联系方式、收款账户、推广链接"
        ]
      },
      {
        heading: "按什么顺序整理最省力",
        paragraphs: [
          "建议按“时间 + 金额 + 渠道”三个维度建立一份简单的表格：每一笔支出都对应一条记录，注明时间、金额、支付渠道和当前状态。这份表格在向多个部门反映情况时可以直接复用，避免每次重新回忆。"
        ]
      },
      {
        heading: "常见误区",
        paragraphs: [
          "很多人只保留了聊天截图，却没有保存支付流水；也有人把维权希望完全寄托在平台客服身上。记住，客服随时可能失联，你自己的材料才是最可靠的依据。",
          "另外，不要因为着急而删除任何对话或转账记录，即使里面包含你不想回看的催促内容，它们同样是证据的一部分。"
        ]
      }
    ]
  },
  {
    id: "ponzi-warning-signals",
    title: "识别资金盘的 7 个危险信号",
    category: "防骗知识",
    categoryKey: "knowledge",
    summary:
      "高息、拉人头、提现门槛，这些特征往往同时出现。学会从收益来源和资金结构上看问题，比记住具体平台名字更有用。",
    readMinutes: 10,
    updatedAt: "2026-08-19",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20about%20ponzi%20scheme%20warning%20signals%2C%20descending%20staircase%20of%20coin%20blocks%20and%20a%20fading%20pyramid%20outline%2C%20crimson%20and%20warm%20charcoal%20palette%2C%20minimal%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text&width=1000&height=640&seq=guide-cover-ponzi-2026&orientation=landscape",
    sections: [
      {
        heading: "信号一：收益来源说不清",
        paragraphs: [
          "任何投资都应该能说明钱投向了哪里、靠什么赚钱。如果对方只强调“稳定高回报”，却无法解释底层资产或业务模式，就应该提高警惕。"
        ]
      },
      {
        heading: "信号二：收益与拉新强绑定",
        paragraphs: [
          "当收益主要来自发展下线而非真实经营，资金结构就已经接近庞氏模式。层级越多、返佣越复杂，风险越高。"
        ]
      },
      {
        heading: "信号三到七",
        paragraphs: [
          "其余值得警惕的信号包括：提现设置复杂门槛、鼓励不断加大投入、宣传中频繁使用绝对化用语、主体信息与资质模糊、以及出现“解冻费、税费”等额外缴费要求。",
          "把这些信号放在一起看，比单独看某一条更有效。真实经营的项目通常不必用紧迫感和高息来推动你尽快转账。"
        ],
        bullets: [
          "提现需要缴费或满足复杂条件",
          "宣传中大量使用“稳赚、保本、零风险”等表述",
          "无法提供可核验的经营主体与业务说明",
          "被反复劝说要“趁活动加大投入”"
        ]
      },
      {
        heading: "已经投入了怎么办",
        paragraphs: [
          "立刻停止追加任何资金，尤其是对方要求的所谓解冻费与保证金。保存全部转账凭证与沟通记录，尽快向公安机关报案，同时向金融监管部门反映。时间越早，资金被拦截的可能性越高。"
        ]
      }
    ]
  },
  {
    id: "refund-complaint-flow",
    title: "网购退款维权：从协商到投诉的完整流程",
    category: "维权流程",
    categoryKey: "process",
    summary:
      "从与商家协商、平台介入到行政投诉，每一步该说什么、该准备什么材料，这份流程梳理帮你少走弯路。",
    readMinutes: 9,
    updatedAt: "2026-08-11",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20about%20ecommerce%20refund%20process%2C%20a%20flowing%20path%20of%20rounded%20steps%20with%20arrows%20and%20a%20package%20icon%2C%20teal%20amber%20and%20charcoal%20palette%2C%20minimal%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text&width=1000&height=640&seq=guide-cover-refund-2026&orientation=landscape",
    sections: [
      {
        heading: "第一步：把诉求写清楚",
        paragraphs: [
          "协商时避免情绪化表达，把诉求浓缩成一句话：订单号 + 问题 + 你希望的解决方案。例如“订单 2026xxxx 未发货，希望全额退款”。清晰的诉求更容易被处理和留档。"
        ]
      },
      {
        heading: "第二步：逐级升级",
        paragraphs: [
          "先在商家处协商并保留记录；协商无果后申请平台介入；平台处理不满意时，再向行政投诉渠道反映。每一级都要附上上一级的沟通记录，形成完整链路。"
        ],
        bullets: [
          "商家协商：保留聊天与工单编号",
          "平台介入：提交订单号与证据，明确诉求",
          "行政投诉：填写商家名称、问题描述与诉求",
          "支付渠道：在时限内发起交易争议申请"
        ]
      },
      {
        heading: "第三步：注意时间窗口",
        paragraphs: [
          "不同渠道有不同的受理时限。支付渠道的交易争议往往有明确的时间窗口，越早发起越好；行政投诉则需要完整的信息，包括商家主体名称与联系方式。"
        ]
      },
      {
        heading: "第四步：记录与跟进",
        paragraphs: [
          "每一次沟通后，记录时间、对接方、结论与下一步。维权周期可能较长，一份清晰的跟进记录能帮你不遗漏任何一个环节，也方便在需要时向更高层级反映。"
        ]
      }
    ]
  },
  {
    id: "parttime-scam-why",
    title: "兼职刷单为什么几乎都是骗局",
    category: "防骗知识",
    categoryKey: "knowledge",
    summary:
      "刷单本身就不被允许，而所有要求先垫付的模式，本质都是把风险留给你。理解这个逻辑，比记住话术更管用。",
    readMinutes: 7,
    updatedAt: "2026-07-30",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20about%20fake%20online%20part%20time%20job%2C%20chat%20message%20bubbles%20and%20a%20hand%20reaching%20for%20coins%20with%20distorted%20shapes%2C%20muted%20teal%20and%20amber%20palette%2C%20minimal%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text&width=1000&height=640&seq=guide-cover-parttime-2026&orientation=landscape",
    sections: [
      {
        heading: "刷单的底层逻辑",
        paragraphs: [
          "真实的兼职是用劳动换取报酬，而刷单返现的报酬来自你后续投入的本金。前期的小额返现只是建立信任的成本，一旦金额放大，平台就有充分的动机不再返还。"
        ]
      },
      {
        heading: "为什么“先垫付”一定危险",
        paragraphs: [
          "任何要求你先付出资金的兼职，都把损失风险完全转移给了你。即便任务看起来再合理，只要涉及垫付、充值、缴纳保证金，就应当立即停止。"
        ]
      },
      {
        heading: "遇到时怎么处理",
        paragraphs: [
          "不要继续完成任务，也不要被“差一单就全部返还”的说法说服。保存聊天记录与转账凭证，联系银行或支付平台尝试止付，并尽快到就近派出所报案。"
        ]
      }
    ]
  },
  {
    id: "prepaid-card-risk",
    title: "预付卡消费，怎样降低跑路风险",
    category: "消费提醒",
    categoryKey: "tips",
    summary:
      "健身、美容、餐饮、洗车，预付卡渗透在生活各处。掌握几条原则，可以在享受折扣的同时把风险控制在可承受范围内。",
    readMinutes: 6,
    updatedAt: "2026-07-18",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20about%20prepaid%20membership%20card%20safety%2C%20a%20protective%20shield%20over%20layered%20rounded%20card%20shapes%2C%20deep%20teal%20and%20soft%20amber%20palette%2C%20minimal%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text&width=1000&height=640&seq=guide-cover-prepaid-2026&orientation=landscape",
    sections: [
      {
        heading: "原则一：控制单次投入",
        paragraphs: [
          "折扣再诱人，也不要一次性投入超出自己一年内确定会消费掉的金额。小额多次充值虽然折扣略低，但把风险拆散了。"
        ]
      },
      {
        heading: "原则二：看清退款条款",
        paragraphs: [
          "充值前确认是否可退、是否可转让、跨店使用是否受限。把这些条款拍照留存，避免事后各说各话。"
        ]
      },
      {
        heading: "原则三：关注经营变化",
        paragraphs: [
          "门店数量减少、员工频繁更换、服务质量下降，往往是经营压力的早期信号。发现异常时尽快消费余额或提出退费申请。"
        ],
        bullets: [
          "优先选择经营时间较长、门店稳定的商家",
          "避免向个人账户转账充值",
          "保留充值凭证与消费记录"
        ]
      }
    ]
  },
  {
    id: "report-channels-list",
    title: "收藏这份网络诈骗举报渠道清单",
    category: "维权流程",
    categoryKey: "process",
    summary:
      "不同情况对应不同渠道。把这份清单收藏起来，需要时按顺序对照提交，避免在多个部门之间来回奔波。",
    readMinutes: 5,
    updatedAt: "2026-09-01",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20about%20official%20reporting%20channels%2C%20a%20grid%20of%20rounded%20panel%20shapes%20connected%20by%20lines%20with%20small%20document%20icons%2C%20amber%20charcoal%20and%20teal%20palette%2C%20minimal%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text&width=1000&height=640&seq=guide-cover-channels-2026&orientation=landscape",
    sections: [
      {
        heading: "涉及资金被骗",
        paragraphs: [
          "第一时间联系银行或支付平台申请止付、冻结相关账户，随后携带转账凭证、聊天记录到就近派出所报案，并保留报案回执。动作越快，资金被拦截的概率越高。"
        ]
      },
      {
        heading: "涉及消费纠纷",
        paragraphs: [
          "商品或服务本身的问题，可先走平台售后，再向消费者投诉举报渠道反映，提交时写清商家名称、订单信息、问题描述与具体诉求。"
        ]
      },
      {
        heading: "涉及虚假宣传与违规经营",
        paragraphs: [
          "对存在虚假宣传、无证经营、违规收费等情况的，可向对应的行业主管部门反映，附上截图证据与时间线说明。"
        ],
        bullets: [
          "资金风险优先联系银行与公安机关",
          "消费纠纷优先平台售后与消费者投诉渠道",
          "行业违规向对应主管部门反映"
        ]
      }
    ]
  }
];

export const guideCategories = [
  { key: "all", name: "全部指南" },
  { key: "process", name: "维权流程" },
  { key: "knowledge", name: "防骗知识" },
  { key: "tips", name: "消费提醒" }
];